import { type EnhancedLogisticOfferForSale } from '@invivodf/module-logistic-offer-client-sdk'
import { ShippingMethodKind } from '@invivodf/module-logistic-offer-client-sdk/dist/convert-logistic-route-to-shipping-method-kind/convert-logistic-route-to-shipping-method-kind'
import { uniqBy } from 'es-toolkit/array'
import { type Offer } from '@b2ag/offers/src/offer'
import { hasDiscount } from '@b2ag/offers/src/offer.business'
import { type CooperativeVariant, type ProductRedirect } from './product'
import { getProductMainImage } from './products-images.constants'

// Dev note: Keys of the product identity section
const identityKeys = new Set(['type', 'contribution_mode', 'standard', 'valuation', 'genetic_profil'])

// Dev note: Keys of the product detail section (The identity section is in the detail section)
const detailKeys = new Set(
  [
    'chemicals_elements',
    'toxicities',
    'engraisPackagingForm',
    'description',
    'active_substances',
    'toxicities',
    'associatedUses',
    'toxicities',
    'type',
    'contribution_mode',
    'standard',
    'characteristics',
  ].concat(Array.from(identityKeys)),
)

export const NB_DAYS_BEFORE_VISIBLE = 10
export const MAX_DATE_TIME = new Date(864000000000000)

export function getProductGenericLabel(product: any): string | undefined {
  return product && (product.kind !== 'services' ? 'produit' : 'service')
}

export function hasOffers(product: any): boolean {
  return product?.variants.some(({ offers = [] }) => offers.length > 0)
}

export function hasDetail(product: any) {
  return product && Object.entries(product).some(([key, value]) => detailKeys.has(key) && value)
}

export function hasImage(product: any) {
  return !!(product && getProductMainImage(product.medias, false))
}

export function getProductTypeNoOfferLabel(product: any) {
  return product && (product.kind !== 'services' ? 'conditionnement' : 'service')
}

export function isOffSeason(product: any): boolean {
  return !!product?.variants?.some((variant: CooperativeVariant & { offers?: Offer[] }) =>
    variant.offers?.some((offer: Offer) => offer.offerType === 'off_season'),
  )
}

export function isOnDiscount(product: any): boolean {
  return !!product?.variants?.some((variant: CooperativeVariant & { offers?: Offer[] }) =>
    variant.offers?.some(hasDiscount),
  )
}

export function isCalculableProduct(product: any) {
  return product?.kind === 'semences'
}

export function hasDocumentation(product: any) {
  if (product?.documentation && Object.keys(product.documentation).length > 0) {
    return (Object.values(product.documentation) as string[][]).some((urls) => urls && urls.length > 0)
  }
  return (
    product?.safetySheetUrls?.length >= 1 ||
    product?.productManualUrls?.length >= 1 ||
    product?.productSheetUrls?.length >= 1
  )
}

export function hasCooperativeReview(product: any) {
  return !!product?.cooperativeReview?.tips || !!product?.cooperativeReview?.upsides
}

export function hasUsages(product: any) {
  return !!(product?.associatedUses?.length > 0)
}

function removeClickAndCollect(logisticOffers: EnhancedLogisticOfferForSale[]) {
  return logisticOffers.filter(
    (logisticOffer) => logisticOffer.shippingMethodKind !== ShippingMethodKind.ClickAndCollect,
  )
}

function allowedWithoutStockVerification(offer: Offer) {
  if (offer.allowedWithoutStock) {
    return removeClickAndCollect(offer.logisticOffers || [])
  }
  return offer.logisticOffers || []
}

export function getProductLogisticOffers(product: any) {
  const logisticOffers =
    product.variants?.flatMap((variant: CooperativeVariant & { offers?: Offer[] }) => {
      const deliveryModeForThisVariant =
        variant.offers?.flatMap((offer) => allowedWithoutStockVerification(offer)) || []
      return variant.stockManagementType === 'notManaged'
        ? removeClickAndCollect(deliveryModeForThisVariant)
        : deliveryModeForThisVariant
    }) || []
  return uniqBy(logisticOffers, (item: any) => item.reference)
}

function endOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

export function offerRemainingDays(
  offerValidityEndDate: Date | null | undefined,
  discountValidityEndDate: Date | null | undefined,
): number {
  const offerRealValidityEndDate = offerValidityEndDate ? endOfDay(offerValidityEndDate) : MAX_DATE_TIME
  const discountRealValidityEndDate = discountValidityEndDate ? endOfDay(discountValidityEndDate) : MAX_DATE_TIME
  const expiredDate =
    offerRealValidityEndDate < discountRealValidityEndDate ? offerRealValidityEndDate : discountRealValidityEndDate
  const today = new Date()
  const remainingDays = Math.ceil((expiredDate.getTime() - today.getTime()) / 86_400_000)
  return remainingDays <= NB_DAYS_BEFORE_VISIBLE ? remainingDays : Infinity
}

export function isProductRedirectUrl(object: any): object is ProductRedirect {
  return object && 'redirectUrl' in object
}

export function isExcluTech(product: any): boolean {
  if (!product) {
    return false
  }

  const hasRestrictedVariant =
    product.variants &&
    product.variants.length > 0 &&
    product.variants.some(
      (variant: CooperativeVariant) =>
        variant.restrictedToProfiles?.length === 1 &&
        !variant.offline &&
        variant.sellable &&
        variant.restrictedToProfiles?.includes('sales'),
    )

  const allOffers =
    product.variants?.flatMap((variant: CooperativeVariant & { offers?: Offer[] }) => variant.offers || []) || []

  const hasTechExclusiveOffer = allOffers.length > 0 && allOffers.some((offer: Offer) => !!offer.isTechExclusive)

  return hasRestrictedVariant || hasTechExclusiveOffer
}
